/**
 * ACCOUNT PROGRESSION — lifetime XP, levels and rank tiers.
 *
 * This is the SINGLE source of truth for the curve. The PURGE rank formula
 * used to be hand-copied into both the gameserver and the API (each with a
 * comment noting it had to match the other) — that duplication is exactly the
 * kind of thing that silently drifts, so everything lives here now and both
 * sides import it.
 *
 * Shared code only: no DB, no THREE, no colyseus. Safe on client and server.
 */
/**
 * Cost to go from level n to n+1 is BASE*n, but the increment STOPS growing at
 * LEVEL_CAP. A pure quadratic curve keeps steepening forever — it put the top
 * tier at roughly 4000 matches, which is a grind nobody finishes. Capping the
 * increment makes the tail linear (~9 matches per level), so high ranks stay a
 * long-term goal instead of an unreachable one.
 */
const LEVEL_BASE = 100;
const LEVEL_CAP = 20;
/** XP to climb from `level` to `level + 1`. */
export function xpToNext(level) {
    return LEVEL_BASE * Math.min(Math.max(1, Math.floor(level)), LEVEL_CAP);
}
/** Total XP required to have REACHED a given level (level 1 = 0 xp). */
export function xpForLevel(level) {
    const l = Math.max(1, Math.floor(level)) - 1; // levels completed
    const q = Math.min(l, LEVEL_CAP);
    const quad = LEVEL_BASE * q * (q + 1) / 2; // the growing part
    const flat = LEVEL_BASE * LEVEL_CAP * Math.max(0, l - LEVEL_CAP); // the linear tail
    return quad + flat;
}
/** Level for a given lifetime XP total (1-based, uncapped). */
export function levelForXp(xp) {
    const x = Math.max(0, xp);
    const kneeXp = xpForLevel(LEVEL_CAP + 1);
    if (x >= kneeXp) {
        // linear tail: every level costs BASE*CAP
        return LEVEL_CAP + 1 + Math.floor((x - kneeXp) / (LEVEL_BASE * LEVEL_CAP));
    }
    // quadratic head: solve BASE*l*(l+1)/2 <= x for the largest integer l
    const l = Math.floor((Math.sqrt(1 + 8 * x / LEVEL_BASE) - 1) / 2);
    return l + 1;
}
export function levelProgress(xp) {
    const level = levelForXp(xp);
    const floor = xpForLevel(level);
    const span = xpForLevel(level + 1) - floor;
    const into = Math.max(0, xp - floor);
    return { level, into, span, frac: span > 0 ? Math.min(1, into / span) : 0 };
}
export const RANK_TIERS = [
    { minLevel: 1, name: "SCRAP", color: "#8a94a6" },
    { minLevel: 5, name: "RUNNER", color: "#5ad2ff" },
    { minLevel: 10, name: "CIRCUIT", color: "#35e6c3" },
    { minLevel: 16, name: "VOLT", color: "#7dff5a" },
    { minLevel: 23, name: "PLASMA", color: "#ffd23d" },
    { minLevel: 31, name: "NOVA", color: "#ff9a3d" },
    { minLevel: 40, name: "PHANTOM", color: "#ff5ad2" },
    { minLevel: 50, name: "OVERLORD", color: "#b45aff" },
    { minLevel: 62, name: "SINGULARITY", color: "#ff3d5a" },
];
export function tierForLevel(level) {
    let best = RANK_TIERS[0];
    for (const t of RANK_TIERS)
        if (level >= t.minLevel)
            best = t;
    return best;
}
export function tierForXp(xp) {
    return tierForLevel(levelForXp(xp));
}
export const XP_RULES = {
    kill: 12,
    headshot: 6, // on top of the kill
    win: 150,
    /** participation, so a losing player still climbs */
    matchPlayed: 40,
    /** per minute played, capped by playtimeCapMin */
    perMinute: 4,
    playtimeCapMin: 25,
    tier: 8,
    purgeRound: 10,
};
/** Derive the level-up card from earned XP + new lifetime total. Side-effect
 *  free and shared, so the client display and the offline bench agree exactly. */
export function summarizeXpAward(earned, total) {
    const e = Math.max(0, Math.round(earned));
    const after = Math.max(0, Math.round(total));
    const before = Math.max(0, after - e);
    const prevLevel = levelForXp(before);
    const level = levelForXp(after);
    const prog = levelProgress(after);
    const prevTier = tierForLevel(prevLevel);
    const tier = tierForLevel(level);
    return {
        earned: e,
        total: after,
        prevLevel,
        level,
        levelsGained: Math.max(0, level - prevLevel),
        leveledUp: level > prevLevel,
        prevTier,
        tier,
        rankedUp: tier.name !== prevTier.name,
        into: prog.into,
        span: prog.span,
        frac: prog.frac,
    };
}
/** XP awarded for one finished match. Deterministic and side-effect free. */
export function matchXp(m) {
    const mins = Math.min(XP_RULES.playtimeCapMin, Math.max(0, m.playtimeS) / 60);
    let xp = XP_RULES.matchPlayed;
    xp += Math.max(0, m.kills) * XP_RULES.kill;
    xp += Math.max(0, m.headshots) * XP_RULES.headshot;
    xp += Math.max(0, m.bestTier) * XP_RULES.tier;
    xp += mins * XP_RULES.perMinute;
    xp += Math.max(0, m.purgeRound ?? 0) * XP_RULES.purgeRound;
    if (m.won)
        xp += XP_RULES.win;
    return Math.round(xp);
}
//# sourceMappingURL=progression.js.map